import {
  type Dictionary,
  type DictionarySource,
  loadScriptTables,
  type ScriptTables,
} from "@kensio/pinyinjs";

import { DictionaryLoader } from "../segmenting/dictionary.js";
import { element } from "./dom.js";

/**
 * Fetching what the script converter needs, and saying so while it happens.
 *
 * Two files rather than one. The dictionary is what makes the conversion
 * accurate, because 发 is 發 or 髮 and only the reading of the word it sits in
 * says which; the tables are the mapping itself. The package keeps them apart
 * so that converting hanzi to pinyin costs nothing for the tables, which means
 * this page is the one that pays for both.
 *
 * Only the standard tier is offered here. What the full tier adds is four
 * hundred thousand mostly-name entries, and names are where script conversion
 * has least to decide, so 2.2 MB would buy this page close to nothing.
 */

/** Everything a conversion needs, once it has arrived. */
export interface ScriptResources {
  readonly dictionary: Dictionary;
  readonly tables: ScriptTables;
}

/**
 * The page's dictionary and script tables, and the status line reporting them.
 */
export class ScriptLoading {
  readonly #dictionaries: DictionaryLoader;
  readonly #source: DictionarySource;
  readonly #status: HTMLElement;

  constructor(dictionaryUrl: string, status: HTMLElement) {
    this.#dictionaries = new DictionaryLoader(dictionaryUrl);
    // The loader's own source rather than a second one over the same URL. The
    // tables sit under the same versioned path as the dictionary and are worth
    // keeping on the device for the same reasons, which a source built here
    // would not do.
    this.#source = this.#dictionaries.source;
    this.#status = status;
  }

  #say(message: string): void {
    this.#status.replaceChildren(
      ...(message === "" ? [] : [element("span", { text: message })]),
    );
    this.#status.hidden = message === "";
  }

  /**
   * Fetch both files, or report on the page that it could not be done.
   *
   * Concurrently, because they are independent and on a browser connection the
   * round trips are most of the wait. A failure is reported rather than
   * thrown: there is nothing the caller could do about it that this has not
   * already done, and a converter with no dictionary is one that says so
   * rather than one that crashes.
   */
  async load(): Promise<ScriptResources | undefined> {
    this.#say("Loading the dictionary and the conversion tables…");

    try {
      const [dictionary, tables] = await Promise.all([
        this.#dictionaries.load("standard"),
        loadScriptTables(this.#source),
      ]);
      this.#say("");

      return { dictionary, tables };
    } catch {
      this.#say(
        "The converter could not be loaded. Check your connection and reload the page.",
      );

      return undefined;
    }
  }
}
